import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Modal, Button, message } from 'antd';
import { BrowserMultiFormatReader } from '@zxing/library';
import { SyncOutlined } from '@ant-design/icons';

const BarcodeScannerModal = ({ isVisible, onClose }) => {
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const codeReaderRef = useRef(null);
    const [facingMode, setFacingMode] = useState('environment');
    const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
    const [error, setError] = useState(null);

    const resetState = () => {
        setError(null);
    };

    useEffect(() => {
        const checkCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                setHasMultipleCameras(videoDevices.length > 1);
                console.log(`Detected ${videoDevices.length} video input devices`);
            } catch (error) {
                console.error('Error checking for multiple cameras:', error);
                setHasMultipleCameras(false);
            }
        };
        checkCameras();
    }, []);

    useEffect(() => {
        if (isVisible) {
            resetState();
        }
    }, [isVisible]);

    useEffect(() => {
        const startScanning = async () => {
            codeReaderRef.current = new BrowserMultiFormatReader();
            try {
                console.log(`Attempting to access camera with facing mode: ${facingMode}`);
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: facingMode,
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    }
                });
                console.log('Camera access granted');
                videoRef.current.srcObject = stream;
                streamRef.current = stream;
                await videoRef.current.play();
                try {
                    const result = await codeReaderRef.current.decodeOnceFromStream(stream, videoRef.current);
                    onClose(result.text);
                } catch (decodeError) {
                    console.error('Error decoding barcode:', decodeError);
                    // Don't set error or close modal here, allow user to try scanning again
                }
            } catch (error) {
                console.error('Error accessing camera:', error);
                setError(`Failed to start camera: ${error.message}`);
                message.error('Failed to access camera. Please check your browser settings and permissions.');
            }
        };

        const stopScanning = () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            if (codeReaderRef.current) {
                codeReaderRef.current.reset();
                codeReaderRef.current = null;
            }
        };

        if (isVisible) {
            startScanning();
        }

        return () => {
            stopScanning();
        };
    }, [isVisible, onClose, facingMode]);

    const toggleCamera = () => {
        setFacingMode(prevMode => prevMode === 'environment' ? 'user' : 'environment');
    };

    const handleCancel = () => {
        resetState();
        onClose(null);
    };

    const modalFooter = useMemo(() => {
        const buttons = [];
        if (hasMultipleCameras) {
            buttons.push(
                <Button key="flip" onClick={toggleCamera} icon={<SyncOutlined />}>
                    Flip Camera
                </Button>
            );
        }
        buttons.push(
            <Button key="cancel" onClick={handleCancel}>
                Cancel
            </Button>
        );
        return buttons;
    }, [hasMultipleCameras, toggleCamera, handleCancel]);

    return (
        <Modal
            title="Scan Barcode"
            visible={isVisible}
            onCancel={handleCancel}
            footer={modalFooter}
            closable={true}
        >
            {error ? (
                <div style={{ color: 'red', marginBottom: '10px' }}>{error}</div>
            ) : (
                <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline />
            )}
        </Modal>
    );
};

export default BarcodeScannerModal;